import React from 'react';

const FlowProfileFinder = ({ createdAt, userName }) => {
  const flowProfiles = [
    {
      name: 'Deep Thinker',
      description: 'The solitary analyst who finds flow in deep, uninterrupted focus.',
      borderColor: 'border-[#006494]',
      bgColor: 'bg-[#006494]/10',
      textColor: 'text-gray-800',
    },
    {
      name: 'Hard Charger',
      description: 'The bold risk-taker who finds flow on the edge of high consequence.',
      borderColor: 'border-[#A84B2F]',
      bgColor: 'bg-[#A84B2F]/10',
      textColor: 'text-gray-800',
    },
    {
      name: 'Flow Goer',
      description: 'The open, adaptable explorer who finds flow through novelty and ease.',
      borderColor: 'border-[#20808D]',
      bgColor: 'bg-[#20808D]/10',
      textColor: 'text-gray-800',
    },
    {
      name: 'Crowd Pleaser',
      description: 'The connected collaborator who finds flow in shared, social momentum.',
      borderColor: 'border-[#7A39BB]',
      bgColor: 'bg-[#7A39BB]/10',
      textColor: 'text-gray-800',
    },
  ];

  const scaleItems = [
    { score: '1', meaning: 'Strongly Disagree' },
    { score: '2', meaning: 'Disagree' },
    { score: '3', meaning: 'Neutral' },
    { score: '4', meaning: 'Agree' },
    { score: '5', meaning: 'Strongly Agree' },
  ];

  return (
    <>
      <main className="py-10 space-y-10 bg-white">
        <section className="flex flex-col space-y-10 ">
          <div className="flex flex-col space-y-8 text-left">
            <div className="flex justify-start items-start pt-2 pl-28">
              <img src="/img/logo/mile_logo.png" alt="Logo" className="w-56" />
            </div>
            <div className="py-2 space-y-4 text-left pl-28">
              <h1 className="text-6xl text-gray-900 font-bold text-left items-center justify-center">FLOW PROFILE <br /><span className="text-gray-900 font-medium">REPORT</span></h1>
              <h1 className="text-lg font-semibold text-[#009688]">Understanding how you naturally access focus, <br /> engagement, and peak performance.</h1>
            </div>
            <div className="flex justify-center pt-6">
              <img src="/userdashboard/flow-profile.png" alt="flow-profile" className="w-full h-full mx-auto" />
            </div>

            <div className="text-left pl-32 pt-12">
              <h2 className="flex items-center space-x-4 text-lg text-gray-900">
                <span className='w-12 font-bold'>Name:</span>
                <span className='font-medium'>{userName}</span>
              </h2>
              <h2 className="flex items-center space-x-4 text-lg text-gray-900">
                <span className='w-12 font-bold'>Date:</span>
                <span className='font-medium'>{createdAt}</span>
              </h2>
            </div>
            <div className="text-left pl-32 pt-12">
              <p className="text-lg text-[#212121] font-medium">Pitstop Consultancy Pvt. Lts. Online Assessments Copyright 2026</p>
              <p className="text-base font-medium text-[#212121]/70 italic">This report is confidential and should not be distributed without permission</p>
            </div>
          </div>
        </section>
      </main>

      <div className="p-10 px-6 space-y-5 bg-white">
        <div className="px-12 space-y-5 page-break">
          <div className="flex items-center justify-between w-full pt-4 pb-2 border-b text-[#212121]/85 italic">
            <h1 className="text-lg font-semibold">Flow Profile Report</h1>
            <p className="text-lg font-semibold">{userName} | {createdAt}</p>
          </div>

          {/* Title Section */}
          <div className="mt-6 mb-5">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              The Flow Profile Finder
            </h1>
            <p className="text-base text-gray-600 mt-1 font-medium">
              Discover how you enter flow - an alternative assessment inspired by the Flow Genome Project.
            </p>
          </div>

          {/* Intro Paragraphs */}
          <div className="space-y-4 text-base text-gray-600 leading-relaxed">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1.5">
                What is your flow profile?
              </h3>
              <p>
                Flow is the optimal state of consciousness where we feel and perform our best - attention fully absorbed,
                action and awareness merged, self-consciousness and time distortion falling away. But not everyone enters
                flow the same way. The Flow Genome Project's research suggests people gravitate toward distinct 'flow
                profiles': characteristic patterns in how they prefer to work, the risks they tolerate, the triggers that switch
                them on, and how they manage their energy.
              </p>
            </div>

            <p>
              This assessment identifies your dominant pattern across four profiles - the Deep Thinker, the Hard Charger,
              the Flow Goer, and the Crowd Pleaser. Most people are a blend: a primary profile that comes naturally, a
              secondary that supports it, and a least-used profile that represents untapped range. Knowing your profile is
              practical, not just descriptive - it tells you which conditions, triggers, and rituals reliably drop you into flow,
              and which common blockers knock you out of it.
            </p>

            <p>
              This is an original instrument inspired by the Flow Genome framework, designed for modern work: it factors in
              how remote and hybrid work, constant digital connectivity, and AI tools each help or hinder your specific path
              into flow. Answer honestly based on how you actually operate, not how you think you should.
            </p>
          </div>

          {/* Four Flow Profiles Blocks */}
          <div className="mt-8 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              The four flow profiles
            </h3>
            <div className="space-y-2.5">
              {flowProfiles.map((profile, idx) => (
                <div
                  key={idx}
                  className={`p-3 border-l-4 ${profile.borderColor} ${profile.bgColor} rounded-r text-base ${profile.textColor}`}
                >
                  <span className="font-bold">{profile.name}</span>
                  <span className="text-gray-600 mx-2">-</span>
                  <span>{profile.description}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Instructions & Scale Split Layout */}
          {/* <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-start mb-6">
            <div className="md:col-span-3 space-y-3">
              <h3 className="text-lg font-bold text-gray-900">
                How to use this finder
              </h3>
              <ol className="list-decimal pl-4 space-y-2.5 text-base text-gray-600 leading-relaxed">
                <li>Go to the Assessment tab and answer all 20 statements using the 1-5 scale below.</li>
                <li>Answer based on how you actually operate - not how you wish you should. There are no wrong answers.</li>
                <li>Your scores rank automatically on the Your Flow Profile tab: primary, secondary, and least-used.</li>
                <li>Read your matching report in RESULT REPORTS for how you flow, 3 Flow Hacks, and your flow-blockers.</li>
              </ol>
            </div>

            <div className="md:col-span-2">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                The 1-5 Scale
              </h3>
              <div className="border-2 border-white rounded-sm overflow-hidden">
                <table className="w-full text-center text-base border-collapse">
                  <thead>
                    <tr className="bg-sky-800 text-white font-semisemibold">
                      <th className="p-2 border-r-2 border-white w-1/4">Score</th>
                      <th className="p-2">Meaning</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y-2 divide-white">
                    {scaleItems.map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 1 ? 'bg-gray-100' : 'bg-gray-100'}>
                        <td className="p-2 font-bold text-gray-800 border-r-2 border-white">{item.score}</td>
                        <td className="p-2 text-gray-900 font-medium">{item.meaning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div> */}

          {/* Footer Framework Attribution */}
          {/* <p className="text-sm text-gray-700 leading-relaxed">
            An original instrument inspired by the Flow Genome Project's flow-profile framework (Steven Kotler, Jamie Wheal). Not a reproduction of any proprietary Flow Genome assessment.
          </p> */}
        </div>
      </div>
    </>
  );
};

export default FlowProfileFinder;